import { useEffect, useRef, useState } from 'react';
import { useWorldStore } from '../state/worldStore';
import { loadEnsemble, lineClip } from '../services/ensemble';
import { audioEngine } from '../services/audioEngine';

// EnsembleDirector — runs the room's own conversation.
//
// Khari, 2026-08-03: the ensemble is "positioned in places they never have to
// walk from. Their only movement: a looped idle animation + a talking animation,
// facing or not facing the person they're talking to."
//
// So this draws nothing and moves nobody. It owns a TIMELINE and publishes one
// line at a time to the store; InteriorResidents turns that into a bubble over
// the right head and a body turning to the right person. Separating them is what
// lets the same script play over a different cast — the director never learns
// which mesh is standing where.
//
// ONE CHAIN OF TIMERS, ONE AUDIO ELEMENT, and everything else derived from them.
// The failure mode this shape exists to prevent: a scene restarted by a
// re-render while the previous run is still walking its own timers, so two
// copies speak over each other at an offset. Every entry point below either
// cancels the chain or is a no-op.

// How long a line takes to say when there is no clip. Roughly 14 characters a
// second — conversational rather than newsreader — with a floor so a three-word
// answer still lands, and a ceiling so a long line cannot hold the room.
const readMs = (t) => Math.min(9000, Math.max(1800, 900 + String(t || '').length * 70));

// Between repeats of a scene. Long enough that the shop is not a loop you can
// hear looping; short enough that walking in during the gap is not an empty room.
const REST_MS = 42000;
// Re-ask the server for the room's scene at most this often. The daily gossip
// changes once a day (project_daily_news_feed) — polling harder buys nothing.
const REFRESH_MS = 6 * 60 * 1000;

export default function EnsembleDirector({ roomId }) {
  const myAvatarId = useWorldStore((s) => s.myAvatarId);
  const say = useWorldStore((s) => s.sayBanter);
  const setEnsemble = useWorldStore((s) => s.setEnsemble);
  // HOLD YOUR TONGUE WHILE SOMEBODY IS TALKING TO THE PLAYER. A directed scene
  // or a voice chat owns the room's attention and its audio; ambient banter
  // underneath it is two conversations at once, which is how a shop reads as
  // noise rather than as a place.
  const busy = useWorldStore((s) => !!s.chatAvatar || !!s.scene || !!s.phoneCall || !!s.shop);

  const [scene, setScene] = useState(null);
  const linesRef = useRef([]);      // the live copy, patched as clips land
  const busyRef = useRef(busy);
  busyRef.current = busy;
  const loadedAt = useRef(0);

  // ── The script ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!roomId) { setScene(null); linesRef.current = []; return undefined; }
    let dead = false;
    (async () => {
      const d = await loadEnsemble(roomId, myAvatarId);
      if (dead) return;
      loadedAt.current = Date.now();
      setEnsemble({ zone: roomId, cast: d.cast, sceneKey: d.scene?.key || null });
      linesRef.current = d.scene?.lines || [];
      setScene(d.scene || null);

      // SAY WHY THE ROOM IS QUIET. Every silent path here looks identical from
      // inside the salon — nobody cast, nothing written, a scene whose
      // requirements you do not meet — and the same lesson as InteriorResidents:
      // a feature that reports nothing by drawing nothing reads as broken.
      const cast = d.cast || [];
      const uncast = cast.filter((c) => !c.known).map((c) => c.actorKey);
      console.log(`[ensemble] "${roomId}": ${cast.length} cast${cast.length ? ` (${cast.map((c) => `${c.role || '?'}=${c.ref}`).join(', ')})` : ''}`
        + ` · scene ${d.scene ? `"${d.scene.key}" ${d.scene.lines.length} line(s)` : 'none'}`);
      if (uncast.length) console.warn(`[ensemble] placement(s) cast as ${uncast.join(', ')} — no such actor_key in world_actors`);
      (d.skipped || []).forEach((s) => console.log(`[ensemble] skipped "${s.scene}" — ${s.why}`));
      if (!cast.length) console.log('[ensemble] nobody in this room is playing a part — set one in ⚙ → Interior avatar → part');
    })();
    return () => { dead = true; };
  }, [roomId, myAvatarId, setEnsemble]);

  // ── Clips that were still rendering when the script arrived ───────────────
  //
  // Authored banter is synthesized once ever and cached, so this only happens on
  // the first visit after a line is written. Patching `linesRef` in place rather
  // than through state is deliberate: a landed clip must not restart the
  // timeline, and the running scene reads its lines from the ref every step.
  useEffect(() => {
    const pending = (scene?.lines || []).map((l, i) => (l.audioKey ? { i, key: l.audioKey } : null)).filter(Boolean);
    if (!pending.length) return undefined;
    let dead = false;
    let tries = 0;
    const tick = async () => {
      if (dead) return;
      tries += 1;
      let left = 0;
      for (const p of pending) {
        const line = linesRef.current[p.i];
        if (!line || line.audioUrl) continue;
        const url = await lineClip(p.key);
        if (dead) return;
        if (url) linesRef.current[p.i] = { ...line, audioUrl: url, audioKey: null };
        else left += 1;
      }
      if (left && tries < 20) setTimeout(tick, 2500);
      else console.log(`[ensemble] clips ready for "${scene.key}"${left ? ` (${left} never landed — those lines stay text)` : ''}`);
    };
    const t = setTimeout(tick, 1500);
    return () => { dead = true; clearTimeout(t); };
  }, [scene]);

  // ── The timeline ──────────────────────────────────────────────────────────
  //
  // Keyed on the scene KEY, not the scene object: a patched clip list must not
  // start the conversation over from the top.
  useEffect(() => {
    if (!scene?.key || !linesRef.current.length) return undefined;
    let dead = false;
    let timer = null;
    let audio = null;
    let i = 0;
    let dipped = false;

    const undip = () => { if (dipped) { dipped = false; audioEngine.dip(false); } };
    const stopClip = () => {
      if (audio) { try { audio.pause(); } catch { /* already gone */ } audio = null; }
      undip();
    };
    // ONE PENDING TIMER, ALWAYS. Every scheduled step replaces the last, which is
    // what keeps the stall backstop below from firing a second advance after the
    // clip's own `ended` already moved the scene on — two chains walking the same
    // script at an offset is exactly the double-speak this component is shaped to
    // prevent, and it would only show up on lines that have audio.
    const at = (ms, fn) => { clearTimeout(timer); timer = setTimeout(fn, ms); };

    const advance = (gap) => {
      clearTimeout(timer);
      stopClip();
      i += 1;
      if (i >= linesRef.current.length) {
        i = 0;
        say(null);                     // the shop goes back to being a shop
        at(REST_MS, start);
        return;
      }
      at(Math.max(250, gap), step);
    };

    const step = () => {
      if (dead) return;
      // A paused scene WAITS where it is rather than skipping ahead: coming back
      // to the middle of an exchange is how you know it was going on without you.
      if (busyRef.current || (typeof document !== 'undefined' && document.hidden)) { at(1500, step); return; }

      const line = linesRef.current[i];
      if (!line) { advance(0); return; }
      say({ actor: line.actor, to: line.to || null, text: line.text, name: line.name || null });

      const gap = line.wait || 400;
      if (!line.audioUrl) { at(readMs(line.text) + gap, () => advance(0)); return; }

      try {
        audio = new Audio(line.audioUrl);
        audio.onended = () => { if (!dead) { undip(); advance(gap); } };
        audio.onerror = () => { if (!dead) { undip(); advance(gap); } };
        if (!dipped) { dipped = true; audioEngine.dip(true); }
        audio.play().catch(() => { /* autoplay refused — the line still reads */ });
        // A clip that never fires `ended` (a decode failure, a tab suspended
        // mid-line) would hold the scene forever. The bubble's own reading time,
        // doubled, is the backstop.
        at(readMs(line.text) * 2 + gap, () => { if (!dead) advance(0); });
      } catch {
        at(readMs(line.text) + gap, () => advance(0));
      }
    };

    const start = () => {
      if (dead) return;
      // Freshen the script between repeats — today's gossip should not need a
      // door to be walked through twice to be heard.
      if (Date.now() - loadedAt.current > REFRESH_MS) {
        loadedAt.current = Date.now();
        loadEnsemble(roomId, myAvatarId).then((d) => {
          if (dead) return;
          if (d.scene?.key && d.scene.key !== scene.key) { linesRef.current = d.scene.lines; setScene(d.scene); return; }
          if (d.scene?.lines?.length) linesRef.current = d.scene.lines;
          step();
        });
        return;
      }
      step();
    };

    // A beat after arrival. Walking into a room that starts talking the instant
    // you cross the threshold reads as a trigger firing; a conversation already
    // in progress is the effect we want, and a short delay buys it.
    at(2200, start);

    return () => {
      dead = true;
      clearTimeout(timer);
      stopClip();
      say(null);
    };
  }, [scene?.key, roomId, myAvatarId, say]);   // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}
